import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { Panel, useReactFlow } from "@xyflow/react";
import { Text } from "@zapier/design-system";
import {
  showCanvasTemplateWidgetAtom,
  edgesAtom,
  nodesAtom,
  zslAtom,
  hasZslAtom,
  emitEventOnTemplateToolbarCloseSetter,
  ContentfulTemplate,
  templateSlugAtom,
  isViewOnlyModeAtom,
  IncomingBackendNode,
  IncomingBackendEdge,
} from "@zapier/canvas-state";

import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import {
  fullyConvertToReactFlowEdge,
  fullyConvertToReactFlowNode,
} from "../../utils/convertCanvasSchemaForReactFlow";
import { CloseButton } from "../CloseButton";
import { TemplateThumbnails } from "../TemplateThumbnails";

import styles from "./CreateCanvasFromTemplateToolbar.module.css";

export function CreateCanvasFromTemplateToolbar() {
  const { emitEventOnSelectTemplate } = useEmitUserInteractionEvents();
  const { fitView } = useReactFlow();
  const setSelectedTemplate = useSetAtom(zslAtom);
  const setNodes = useSetAtom(nodesAtom);
  const setEdges = useSetAtom(edgesAtom);
  const hasZsl = useAtomValue(hasZslAtom);
  const setCloseToolbar = useSetAtom(showCanvasTemplateWidgetAtom);
  const emitEventOnTemplateToolbarClose = useSetAtom(
    emitEventOnTemplateToolbarCloseSetter,
  );
  const selectedTemplateSlug = useAtomValue(templateSlugAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);

  function closeToolbar() {
    setCloseToolbar(false);
    emitEventOnTemplateToolbarClose();
  }

  const handleTemplateClick = useCallback(
    async (template: ContentfulTemplate) => {
      const slug = template.fields.slug;

      emitEventOnSelectTemplate(slug);
      setSelectedTemplate({
        ...template.fields.zsl,
        slug,
        name: template.fields.name,
        description: template.fields.description,
      });

      const nodes = template.fields.zsl.canvases[0].nodes.map((node) =>
        fullyConvertToReactFlowNode(
          node as IncomingBackendNode,
          isViewOnlyMode,
        ),
      );

      setNodes(nodes);

      const edges = template.fields.zsl.canvases[0].edges.map((edge) =>
        fullyConvertToReactFlowEdge(
          edge as IncomingBackendEdge,
          isViewOnlyMode,
        ),
      );
      setEdges(edges);
      setTimeout(() => fitView(), 0); // Gives time for the nodes and edges to render first
    },
    [
      emitEventOnSelectTemplate,
      fitView,
      isViewOnlyMode,
      setEdges,
      setNodes,
      setSelectedTemplate,
    ],
  );

  return (
    <Panel position="bottom-center" className={styles.panel}>
      <div className={styles.root}>
        <span className={styles.titleWrapper}>
          <Text type="SectionHeader">Start with a template</Text>
          <div className={styles.buttonsWrapper}>
            {!hasZsl && (
              <CloseButton
                onClose={closeToolbar}
                label="Close template toolbar"
                size={24}
                data-testid="close-template-toolbar"
              />
            )}
          </div>
        </span>
        <TemplateThumbnails
          selectedTemplateSlug={selectedTemplateSlug}
          handleTemplateClick={handleTemplateClick}
        />
      </div>
    </Panel>
  );
}
